// DetalleCob.jsx - Vista de Detalle de Cobranzas
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown, Search, Tag, Calendar,
  RefreshCw, Filter, X, FilterX, Download, Archive, List,
  DollarSign, Users, Receipt
} from 'lucide-react';
import './DashboardBultos.css';
import './DashboardInventario.css';
import DashboardMenuHeader from '../components/CobranzasMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import FullScreenLoader from './FullScreenLoader';
import * as XLSX from 'xlsx';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const nombresMeses = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"
];

const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA', 'SIN SEGMENTO'];

const filterLabels = {
  CLI_REG_VERD: "Región",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_CANAL_REAL: "Canal Real",
  CLI_SEG_DES: "Segmento",
  CLI_TERRITORIO: "Territorio",
  CLI_PRIN: "Cliente Principal",
  CLI_NOMBRE: "Cliente",
  CLIENTE_ID: "Código Cliente",
  ESPECIALISTA: "Especialista",
  CB_COD_CUENTA: "Cuenta",
  CB_TIPO_ORIGEN: "Origen",
  CB_ANULADO: "Estado",
  CB_TIPO: "Tipo Mov.",
  CB_ORIGEN: "Origen Mov.",
  CB_NRO_DOCUM: "Nro. Documento",
  CB_NRO_COBRO: "Nro. Cobro",
};

const INITIAL_FILTERS = {
  CLI_REG_VERD: [],
  CLI_VEN_DES: [],
  CLI_CANAL: [],
  CLI_CANAL_REAL: [],
  CLI_SEG_DES: [],
  CLI_TERRITORIO: [],
  CLI_PRIN: [],
  CLI_NOMBRE: [],
  CLIENTE_ID: [],
  ESPECIALISTA: [],
  CB_COD_CUENTA: [],
  CB_TIPO_ORIGEN: [],
  CB_ANULADO: [],
  CB_TIPO: [],
  CB_ORIGEN: [],
  CB_NRO_DOCUM: [],
  CB_NRO_COBRO: [],
};

// ─── Formateo ───
const parseNumericES = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && !hasDot) return Number(s.replace(/\./g, '').replace(',', '.'));
  return Number(s.replace(/,/g, ''));
};

const formatNumber = (num, decimals = 0) => {
  const n = parseNumericES(num);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatUSD = (num) => `$${formatNumber(num, 2)}`;
const formatBS = (num) => `Bs. ${formatNumber(num, 2)}`;   // ← NUEVO



// ══════════════════════════════════════════════════════════════
// MultiSelectFilter
// ══════════════════════════════════════════════════════════════
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange, onClearPill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? (options.find(opt => (opt.value || opt) === selectedValues[0])?.label ?? selectedValues[0])
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 200);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onClearPill?.(name)}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const DetalleCob = () => {
  const {
    cobranzasQueryKpis,
    cobranzasQueryDetalle,
    getCobranzasFilters,
    loading: apiLoading,
  } = useQuery();

  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const filtersInitialized = useRef(false);

  // ─── KPIs ───
  const [kpis, setKpis] = useState({
    total_cobrado_usd: 0,
    clientes_unicos: 0,
    total_movimientos: 0,
  });

  // ─── filtros ───
  const currentYear = new Date().getFullYear();
  const currentMonthName = nombresMeses[new Date().getMonth()];

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [selectedMeses, setSelectedMeses] = useState([currentMonthName]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // ─── detalle ───
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailViewMode, setDetailViewMode] = useState('SIMPLIFICADO');
  const [detailSortBy, setDetailSortBy] = useState('CB_FECHA');
  const [detailSortDir, setDetailSortDir] = useState('desc');
  const [buscarText, setBuscarText] = useState('');
  const [buscarDebounced, setBuscarDebounced] = useState('');

  const pageSizeRef = useRef(200);
  const requestRefs = useRef({ kpis: 0, detalle: 0 });


  // ══════════════════════════════════════════════════════════════
  // CARGA INICIAL DE CONFIG
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadConfig = async () => {
      if (filtersInitialized.current) return;
      setLocalLoading(true);

      try {
        const config = await getCobranzasFilters();
        if (!config) throw new Error("Configuración de cobranzas no disponible.");

        const fullOptions = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
          const raw = config.availableOptions?.[key];
          if (Array.isArray(raw)) {
            fullOptions[key] = raw.map(item =>
              typeof item === 'string' ? item : (item?.value || item?.label || '')
            ).filter(Boolean);
          } else {
            fullOptions[key] = [];
          }
        });

        // Si el backend no envía CB_DIA, generamos 1..31 como fallback
        if (!fullOptions.CB_DIA?.length) {
          fullOptions.CB_DIA = Array.from({ length: 31 }, (_, i) => String(i + 1));
        } else {
          // Asegurar orden numérico
          fullOptions.CB_DIA = [...fullOptions.CB_DIA].sort((a, b) => Number(a) - Number(b));
        }

        setAvailableOptions(fullOptions);
        setAvailableYears(config.availableYears || [currentYear]);

        const allSegments = fullOptions.CLI_SEG_DES || [];
        const prefilteredSegments = allSegments
          .map(opt => opt?.value || opt)
          .filter(Boolean)
          .filter(segment =>
            !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
          );

        const defaultFilters = {
          ...INITIAL_FILTERS,
          CLI_SEG_DES: prefilteredSegments,
        };
        setFilters(defaultFilters);

        if (config.currentYear) {
          setSelectedAnios([config.currentYear]);
        } else if (config.availableYears?.[0]) {
          setSelectedAnios([config.availableYears[0]]);
        }

        const dataMonth = config.availableOptions?.COB_MES?.find?.(m => m === currentMonthName);
        setSelectedMeses(dataMonth ? [dataMonth] : [currentMonthName]);

        filtersInitialized.current = true;
        setFiltersReady(true);
      } catch (err) {
        console.error('Error cargando config cobranzas:', err);
        filtersInitialized.current = true;
        setFiltersReady(true);
      } finally {
        setLocalLoading(false);
      }
    };
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ══════════════════════════════════════════════════════════════
  // PAYLOAD BASE
  // ══════════════════════════════════════════════════════════════
  const buildBasePayload = useCallback(() => {
    const mergedFilters = { ...filters };

    if (!mergedFilters.COB_AÑO?.length && selectedAnios.length) {
      mergedFilters.COB_AÑO = selectedAnios.map(String);
    }
    if (!mergedFilters.COB_MES?.length && selectedMeses.length) {
      mergedFilters.COB_MES = selectedMeses;
    }

    return {
      filters: mergedFilters,
      page_anios: selectedAnios.filter(Boolean),
      page_meses: selectedMeses.filter(Boolean),
      user_info: profile
        ? { username: profile.username, cargo: profile.cargo }
        : null,
    };
  }, [filters, selectedAnios, selectedMeses, profile]);


  // ══════════════════════════════════════════════════════════════
  // DEBOUNCE DE BÚSQUEDA
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const t = setTimeout(() => setBuscarDebounced(buscarText), 400);
    return () => clearTimeout(t);
  }, [buscarText]);


  // ══════════════════════════════════════════════════════════════
  // FETCH KPIs
  // ══════════════════════════════════════════════════════════════
  const fetchKpis = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.kpis;
    try {
      const res = await cobranzasQueryKpis(buildBasePayload());
      if (reqId !== requestRefs.current.kpis) return;
      setKpis({
        total_cobrado_usd: parseFloat(res?.total_cobrado_usd || 0),
        clientes_unicos: parseInt(res?.clientes_unicos || 0, 10),
        total_movimientos: parseInt(res?.total_movimientos || 0, 10),
      });
    } catch (e) {
      console.error('❌ Error KPIs cobranzas:', e);
    }
  }, [buildBasePayload, cobranzasQueryKpis, filtersReady]);


  // ══════════════════════════════════════════════════════════════
  // FETCH DETALLE
  // ══════════════════════════════════════════════════════════════
  const fetchDetalle = useCallback(async (page = 1) => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.detalle;
    setDetailLoading(true);

    try {
      const payload = {
        ...buildBasePayload(),
        page,
        page_size: pageSizeRef.current,
        sort_by: detailSortBy,
        sort_dir: detailSortDir,
        buscar: buscarDebounced || null,
      };
      const res = await cobranzasQueryDetalle(payload);

      if (reqId !== requestRefs.current.detalle) return;

      setDetailData(Array.isArray(res?.data) ? res.data : []);
      setDetailPage(res?.page || page);
      setDetailTotal(res?.total || 0);
      setDetailTotalPages(res?.total_pages || 1);
    } catch (e) {
      if (reqId === requestRefs.current.detalle) {
        console.error('❌ Error detalle cobranzas:', e);
        setDetailData([]);
      }
    } finally {
      if (reqId === requestRefs.current.detalle) setDetailLoading(false);
    }
  }, [buildBasePayload, cobranzasQueryDetalle, detailSortBy, detailSortDir, buscarDebounced, filtersReady]);


  // ══════════════════════════════════════════════════════════════
  // EFECTOS
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(() => fetchKpis(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedAnios, selectedMeses, filtersReady]);

  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(() => fetchDetalle(1), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedAnios, selectedMeses, detailSortBy, detailSortDir, buscarDebounced, filtersReady]);


  // ══════════════════════════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════════════════════════
  const handleResetFilters = useCallback(() => {
    const allSegments = availableOptions.CLI_SEG_DES || [];
    const prefilteredSegments = allSegments
      .map(opt => opt?.value || opt)
      .filter(Boolean)
      .filter(segment =>
        !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
      );

    setFilters({
      ...INITIAL_FILTERS,
      CLI_SEG_DES: prefilteredSegments,
    });
  }, [availableOptions]);

  const handleClearSingleFilter = useCallback((name) => {
    setFilters(prev => ({ ...prev, [name]: [] }));
  }, []);

  const handleRefresh = useCallback(async () => {
    setQuerying(true);
    try {
      await Promise.all([fetchKpis(), fetchDetalle(1)]);
    } finally {
      setQuerying(false);
    }
  }, [fetchKpis, fetchDetalle]);

  const handleSort = useCallback((col) => {
    if (detailSortBy === col) {
      setDetailSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setDetailSortBy(col);
      setDetailSortDir('desc');
    }
  }, [detailSortBy]);


  // ══════════════════════════════════════════════════════════════
  // EXPORT EXCEL
  // ══════════════════════════════════════════════════════════════
  const downloadDetailExcel = useCallback(() => {
    if (!detailData.length) {
      alert('No hay datos para exportar');
      return;
    }

    const isSimplificado = detailViewMode === 'SIMPLIFICADO';

    const headers = isSimplificado
      ? ['Nro', 'Fecha', 'Cliente', 'Descripción', 'USD']
      : [
        'Año', 'Mes', 'Nro', 'Fecha', 'Nro Cobro', 'Nro Docum',
        'Cuenta', 'Moneda', 'Tipo', 'Origen', 'Descripción',
        'BS', 'Tasa', 'USD', 'IDB', 'Anulado',
        'Cliente ID', 'Cliente', 'RIF', 'Región', 'Vendedor',
        'Segmento', 'Territorio', 'Especialista',
      ];

    const rows = [headers];

    detailData.forEach(row => {
      if (isSimplificado) {
        rows.push([
          row.CB_NUMERO || '',
          row.CB_FECHA || '',
          row.CLI_NOMBRE || '',
          row.CB_DESCRIP || '',
          parseNumericES(row.CB_USD || 0),
        ]);
        return;
      }
      rows.push([
        row.COB_AÑO || '',
        row.COB_MES || '',
        row.CB_NUMERO || '',
        row.CB_FECHA || '',
        row.CB_NRO_COBRO || '',
        row.CB_NRO_DOCUM || '',
        row.CB_COD_CUENTA || '',
        row.CB_MONEDA || '',
        row.CB_TIPO || '',
        row.CB_TIPO_ORIGEN || '',
        row.CB_DESCRIP || '',
        parseNumericES(row.CB_MONTO_H || 0),
        parseNumericES(row.CB_TASA || 0),
        parseNumericES(row.CB_USD || 0),
        parseNumericES(row.CB_IDB || 0),
        row.CB_ANULADO || '',
        row.CLIENTE_ID || '',
        row.CLI_NOMBRE || '',
        row.CLI_RIF || '',
        row.CLI_REG_VERD || '',
        row.CLI_VEN_DES || '',
        row.CLI_SEG_DES || '',
        row.CLI_TERRITORIO || '',
        row.ESPECIALISTA || '',
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Cobranzas');

    const filename = `Detalle_Cobranzas_${detailViewMode}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [detailData, detailViewMode]);


  // ══════════════════════════════════════════════════════════════
  // CARDS
  // ══════════════════════════════════════════════════════════════
  const kpiCards = useMemo(() => ([
    {
      key: 'cobrado',
      icon: <DollarSign size={22} color="white" />,
      label: 'Total Cobrado',
      value: formatUSD(kpis.total_cobrado_usd),

    },
    {
      key: 'clientes',
      icon: <Users size={22} color="white" />,
      label: 'Clientes',
      value: formatNumber(kpis.clientes_unicos),
    },
    {
      key: 'movimientos',
      icon: <Receipt size={22} color="white" />,
      label: 'Transacciones',
      value: formatNumber(kpis.total_movimientos),

    },
  ]), [kpis]);


  // Opciones del filtro DÍA (para el header)
  const diasOptions = useMemo(() => {
    const dias = availableOptions.CB_DIA || [];
    return dias.map(d => ({ value: String(d), label: String(d) }));
  }, [availableOptions]);


  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };


  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersReady && (
        <FullScreenLoader message="Cargando datos de cobranzas..." />
      )}

      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        {/* ═══ HEADER ═══ */}
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Cobranzas</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button
              className="refresh-button"
              onClick={handleRefresh}
              title="Actualizar Datos"
              disabled={querying}
            >
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>

            <button
              className="mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>

            <div className="date-filters-group">
              {/* ✅ NUEVO: Filtro Día en el header */}
              <MultiSelectFilter
                label="Día"
                name="CB_DIA"
                icon={Calendar}
                options={diasOptions}
                selectedValues={filters.CB_DIA || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                onClearPill={handleClearSingleFilter}
              />
              <MultiSelectFilter
                label="Mes"
                name="meses"
                icon={Calendar}
                options={nombresMeses.map(m => ({ value: m, label: m }))}
                selectedValues={selectedMeses}
                onChange={(_, vals) => setSelectedMeses(vals.length ? [vals[vals.length - 1]] : [])}
              />
              <MultiSelectFilter
                label="Año"
                name="anios"
                icon={Calendar}
                options={availableYears.map(y => ({ value: y, label: String(y) }))}
                selectedValues={selectedAnios}
                onChange={(_, vals) => setSelectedAnios(vals.length ? [vals[vals.length - 1]] : [])}
              />
            </div>
          </div>
        </header>

        {/* ═══ BARRA INFO PERÍODO ═══ */}
        <div className="working-days-bar" style={{ marginTop: 12 }}>
          <div className="wd-item">
            <span>Período:</span> <strong>{selectedMeses.join(', ')} {selectedAnios.join(', ')}</strong>
          </div>
          <div className="wd-separator"></div>
          <div className="wd-item">
            <span>Total registros:</span> <strong>{formatNumber(detailTotal)}</strong>
          </div>
          {filters.CB_DIA?.length > 0 && (
            <>
              <div className="wd-separator"></div>
              <div className="wd-item">
                <span>Días:</span> <strong>{filters.CB_DIA.join(', ')}</strong>
              </div>
            </>
          )}
        </div>

        {/* ═══ SIDEBAR FILTROS ═══ */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros de Cobranzas</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={Tag}
                options={key === 'CB_DIA' ? diasOptions : (availableOptions[key] || [])}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                onClearPill={handleClearSingleFilter}
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ═══ 3 CARDS ═══ */}
        <div
          className="dashboard-indicators2"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          {kpiCards.map(card => (
            <div key={card.key} className="indicator-card2 v-real">
              <div className="card-content">
                <div className="inven-icon-wrapper" style={{ background: card.color }}>
                  {card.icon}
                </div>
                <span className="indicator-label">{card.label}</span>
                <div className="indicator-right-group">
                  <span className="indicator-value">{card.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="animated-divider" />

        {/* ═══ TABLA DETALLE ═══ */}
        <div className="detail-table-container">
          <div className="table-header">
            <h3>
              <List size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Detalle de Cobranzas
            </h3>
            <div className="table-controls" style={{ display: 'flex', gap: 8 }}>
              <div className="search-wrapper" style={{ minWidth: 220, position: 'relative' }}>
                <Search size={14} className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar por factura, cliente, descripción..."
                  value={buscarText}
                  onChange={(e) => setBuscarText(e.target.value)}
                  style={{ paddingLeft: 30 }}
                />
              </div>

              <button
                className="export-table-btn"
                onClick={downloadDetailExcel}
                disabled={detailLoading || detailData.length === 0}
                title="Exportar a Excel"
              >
                <Download size={18} />
              </button>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className={`switch-toggle-btn ${detailViewMode === 'SIMPLIFICADO' ? 'active' : ''}`}
                  style={{ padding: '6px 10px' }}
                  onClick={() => setDetailViewMode('SIMPLIFICADO')}
                >
                  Simplificado
                </button>
                <button
                  className={`switch-toggle-btn ${detailViewMode === 'TOTAL' ? 'active' : ''}`}
                  style={{ padding: '6px 10px' }}
                  onClick={() => setDetailViewMode('TOTAL')}
                >
                  Total
                </button>
              </div>

              <div className="pagination-controls">
                <button
                  onClick={() => fetchDetalle(Math.max(1, detailPage - 1))}
                  disabled={detailPage === 1 || detailLoading}
                >
                  Anterior
                </button>
                <span>Página {detailPage} de {detailTotalPages} ({formatNumber(detailTotal)})</span>
                <button
                  onClick={() => fetchDetalle(detailPage + 1)}
                  disabled={detailPage >= detailTotalPages || detailLoading}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          {detailLoading ? (
            <div className="loading-table">
              <RefreshCw className="spin-anim" size={32} />
              <span>Cargando detalle...</span>
            </div>
          ) : detailData.length === 0 ? (
            <div className="empty-state">
              <Archive size={48} />
              <h4>No hay datos de cobranzas</h4>
              <p>Ajusta los filtros para ver el detalle</p>
            </div>
          ) : (
            <div className="responsive-table-wrapper">
              <table className="detail-table">
                <thead>
                  <tr>
                    {detailViewMode === 'SIMPLIFICADO' ? (
                      <>
                        <th onClick={() => handleSort('CB_NUMERO')} style={{ cursor: 'pointer' }}>Nro</th>
                        <th onClick={() => handleSort('CB_FECHA')} style={{ cursor: 'pointer' }}>Fecha</th>
                        <th onClick={() => handleSort('CLI_NOMBRE')} style={{ cursor: 'pointer' }}>Cliente</th>
                        <th>Descripción</th>
                        <th onClick={() => handleSort('CB_USD')} style={{ cursor: 'pointer' }} className="number">USD</th>
                      </>
                    ) : (
                      <>
                        <th>Año</th>
                        <th>Mes</th>
                        <th>Nro</th>
                        <th>Fecha</th>
                        <th>Nro Cobro</th>
                        <th>Nro Docum</th>
                        <th>Cuenta</th>
                        <th>Moneda</th>
                        <th>Tipo</th>
                        <th>Origen</th>
                        <th>Descripción</th>
                        {/* ✅ Monto D eliminado, Monto H renombrado a BS */}
                        <th className="number">BS</th>
                        <th className="number">Tasa</th>
                        <th className="number">USD</th>
                        <th className="number">IDB</th>
                        <th>Anulado</th>
                        <th>Cliente ID</th>
                        <th>Cliente</th>
                        <th>RIF</th>
                        <th>Región</th>
                        <th>Vendedor</th>
                        <th>Segmento</th>
                        <th>Territorio</th>
                        <th>Especialista</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {detailData.map((row, idx) => {
                    const isSimplificado = detailViewMode === 'SIMPLIFICADO';
                    const usd = parseNumericES(row.CB_USD || 0);
                    const isAnulado = String(row.CB_ANULADO || '').toUpperCase() === 'SI';

                    return (
                      <tr key={`det-cob-${idx}`} style={isAnulado ? { opacity: 0.55 } : undefined}>
                        {isSimplificado ? (
                          <>
                            <td data-label="Nro">{row.CB_NUMERO || '-'}</td>
                            <td data-label="Fecha">{row.CB_FECHA || '-'}</td>
                            <td data-label="Cliente">{row.CLI_NOMBRE || '-'}</td>
                            <td data-label="Descripción" className="truncate" title={row.CB_DESCRIP || ''}>
                              {row.CB_DESCRIP || '-'}
                            </td>
                            <td data-label="USD" className={`number ${usd >= 0 ? 'positive' : 'negative'}`}>
                              {formatUSD(usd)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{row.COB_AÑO || '-'}</td>
                            <td>{row.COB_MES || '-'}</td>
                            <td>{row.CB_NUMERO || '-'}</td>
                            <td>{row.CB_FECHA || '-'}</td>
                            <td>{row.CB_NRO_COBRO || '-'}</td>
                            <td>{row.CB_NRO_DOCUM || '-'}</td>
                            <td>{row.CB_COD_CUENTA || '-'}</td>
                            <td>{row.CB_MONEDA || '-'}</td>
                            <td>{row.CB_TIPO || '-'}</td>
                            <td>{row.CB_TIPO_ORIGEN || '-'}</td>
                            <td className="truncate" title={row.CB_DESCRIP || ''}>{row.CB_DESCRIP || '-'}</td>
                            {/* ✅ BS en vez de Monto D/H */}
                            <td className="number">{formatBS(row.CB_MONTO_H)}</td>
                            <td className="number">{formatNumber(row.CB_TASA, 2)}</td>
                            <td className={`number ${usd >= 0 ? 'positive' : 'negative'}`}>{formatUSD(usd)}</td>
                            <td className="number">{formatNumber(row.CB_IDB, 2)}</td>
                            <td>{row.CB_ANULADO || '-'}</td>
                            <td>{row.CLIENTE_ID || '-'}</td>
                            <td>{row.CLI_NOMBRE || '-'}</td>
                            <td>{row.CLI_RIF || '-'}</td>
                            <td>{row.CLI_REG_VERD || '-'}</td>
                            <td>{row.CLI_VEN_DES || '-'}</td>
                            <td>{row.CLI_SEG_DES || '-'}</td>
                            <td>{row.CLI_TERRITORIO || '-'}</td>
                            <td>{row.ESPECIALISTA || '-'}</td>

                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="status-footer">
          {querying
            ? "SINCRONIZANDO COBRANZAS..."
            : `COBRANZAS: ${selectedMeses.join(', ')} ${selectedAnios.join(', ')} · ${formatNumber(detailTotal)} registros`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default DetalleCob;